import CatastroClient from './catastro-browser.js';
import { PropertyResultsManager } from './PropertyResults.js';

/**
 * Frontend Application Logic
 * Smart Catastro App met cascade dropdowns voor Netlify
 */
export function AppCadastro() {
    return class ViteCatastroApp {
    constructor() {
        this.client = new CatastroClient();
        this.cache = new Map();
        this.cacheTimeout = 10 * 60 * 1000; // 10 minuten
        
        this.selectedProvince = null;
        this.selectedMunicipality = null;
        this.selectedStreet = null;
        this.availableProvinces = [];
        this.availableMunicipalities = [];
        this.availableStreets = [];
        
        // Initialiseer PropertyResults manager
        this.propertyResults = new PropertyResultsManager('property-results-container');
        
        this.init();
    }

    async init() {
        console.log('🚀 Initializing Vite Catastro App...');
        
        try {
            // Test API connection
            await this.testConnection();
            
            // Load provinces
            await this.loadProvinces();
            
            // Event listeners
            this.attachEvents();
            
            // ✅ Try to restore form values
            setTimeout(() => {
                this.restoreFormValues();
            }, 300); // Extra time for DOM initialization
            
            console.log('✅ Vite App initialized successfully');
        } catch (error) {
            console.error('❌ Failed to initialize app:', error);
            this.showError('App initialization failed: ' + error.message);
        }
    }

    async testConnection() {
        this.updateStatus('Verbinding testen...', 'loading');
        
        try {
            const result = await this.client.testConnection();
            
            if (result.success) {
                this.updateStatus('Online ✅', 'online');
                console.log('✅ Direct API Connection OK');
            } else {
                this.updateStatus('Offline ❌', 'offline');
                throw new Error(result.message);
            }
        } catch (error) {
            this.updateStatus('Connection Error ❌', 'offline');
            throw error;
        }
    }

    updateStatus(message, type = '') {
        const statusEl = document.getElementById('status');
        if (statusEl) {
            statusEl.textContent = message;
            
            // Base classes for status
            let classes = 'px-4 py-2 rounded-md font-medium text-sm';
            
            // Add type-specific classes
            switch(type) {
                case 'loading':
                    classes += ' bg-yellow-100 text-yellow-700 border border-yellow-300';
                    break;
                case 'online':
                    classes += ' bg-green-100 text-green-700 border border-green-300';
                    break;
                case 'offline':
                    classes += ' bg-red-100 text-red-700 border border-red-300';
                    break;
                default:
                    classes += ' bg-yellow-100 text-yellow-700 border border-yellow-300';
            }
            
            statusEl.className = classes;
        }
    }

    // Cache helpers
    getCached(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.data;
        }
        return null;
    }

    setCached(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    async loadProvinces() {
        const input = document.getElementById('province-input');
        const dropdown = document.getElementById('province-dropdown');
        const countEl = document.getElementById('provinces-count');

        if (input) {
            input.placeholder = 'Laden provincies...';
            input.disabled = true;
        }
        if (countEl) {
            countEl.textContent = 'Laden...';
        }
        if (dropdown) {
            dropdown.classList.add('hidden');
        }
        
        try {
            let provinces = this.getCached('provinces');
            
            if (!provinces) {
                console.log('🔍 Loading provinces via Vite proxy...');
                provinces = await this.client.getProvinces();
                this.setCached('provinces', provinces);
                console.log(`✅ Loaded ${provinces.length} provinces (fresh)`);
            } else {
                console.log(`✅ Loaded ${provinces.length} provinces (cached)`);
            }
            
            // Sorteer provincies alfabetisch
            provinces.sort((a, b) => a.name.localeCompare(b.name));
            
            // Store provinces for autocomplete
            this.availableProvinces = provinces;
            
            input.disabled = false;
            input.placeholder = 'Type minimaal 2 karakters...';
            countEl.textContent = provinces.length;
            
        } catch (error) {
            input.placeholder = 'Error loading provinces';
            countEl.textContent = 'Error';
            console.error('❌ Error loading provinces:', error);
            throw error;
        }
    }

    async loadMunicipalities(provinceCode, provinceName) {
        const input = document.getElementById('municipality-input');
        const dropdown = document.getElementById('municipality-dropdown');
        const countEl = document.getElementById('municipalities-count');
        
        // Reset dependent dropdowns
        this.resetStreet();
        document.getElementById('search-btn').disabled = true;
        
        input.placeholder = 'Laden gemeenten...';
        input.disabled = true;
        input.value = '';
        countEl.textContent = 'Laden...';
        dropdown.classList.add('hidden');
        
        try {
            const cacheKey = `municipalities_${provinceCode}`;
            let municipalities = this.getCached(cacheKey);
            
            if (!municipalities) {
                console.log(`🔍 Loading municipalities for ${provinceName}...`);
                municipalities = await this.client.getMunicipalities(provinceCode);
                this.setCached(cacheKey, municipalities);
                console.log(`✅ Loaded ${municipalities.length} municipalities for ${provinceName}`);
            } else {
                console.log(`✅ Loaded ${municipalities.length} municipalities for ${provinceName} (cached)`);
            }
            
            if (municipalities.length > 0) {
                // Sorteer gemeenten alfabetisch
                municipalities.sort((a, b) => a.name.localeCompare(b.name));
                
                // Store municipalities for autocomplete
                this.availableMunicipalities = municipalities;
                
                input.disabled = false;
                input.placeholder = 'Type minimaal 2 karakters...';
            } else {
                input.placeholder = 'Geen gemeenten gevonden';
                this.availableMunicipalities = [];
            }
            
            countEl.textContent = municipalities.length;
            
        } catch (error) {
            input.placeholder = 'Error loading municipalities';
            countEl.textContent = 'Error';
            console.error('❌ Error loading municipalities:', error);
            this.showError(`Fout bij laden gemeenten voor ${provinceName}: ${error.message}`);
        }
    }

    async loadStreets(provinceCode, municipalityCode, municipalityName) {
        const input = document.getElementById('street-input');
        const dropdown = document.getElementById('street-dropdown');
        const countEl = document.getElementById('streets-count');
        const searchBtn = document.getElementById('search-btn');
        
        searchBtn.disabled = true;
        input.placeholder = 'Laden straten...';
        input.disabled = true;
        countEl.textContent = 'Laden...';
        dropdown.classList.add('hidden');
        
        try {
            const cacheKey = `streets_${provinceCode}_${municipalityCode}`;
            let streets = this.getCached(cacheKey);
            
            if (!streets) {
                console.log(`🔍 Loading streets for ${municipalityName}...`);
                streets = await this.client.getStreets(provinceCode, municipalityCode);
                this.setCached(cacheKey, streets);
                console.log(`✅ Loaded ${streets.length} streets for ${municipalityName}`);
            } else {
                console.log(`✅ Loaded ${streets.length} streets for ${municipalityName} (cached)`);
            }
            
            if (streets.length > 0) {
                // Sorteer straten alfabetisch
                streets.sort((a, b) => a.name.localeCompare(b.name));
                
                // Store streets for autocomplete
                this.availableStreets = streets;
                
                input.disabled = false;
                input.placeholder = 'Type minimaal 2 karakters...';
                document.getElementById('numero').disabled = false;
            } else {
                input.placeholder = 'Geen straten gevonden';
                this.availableStreets = [];
            }
            
            countEl.textContent = streets.length;
            
        } catch (error) {
            input.placeholder = 'Error loading streets';
            countEl.textContent = 'Error';
            console.error('❌ Error loading streets:', error);
            this.showError(`Fout bij laden straten voor ${municipalityName}: ${error.message}`);
        }
    }

    filterProvinces(query) {
        if (!this.availableProvinces || query.length < 2) {
            return [];
        }
        
        const lowerQuery = query.toLowerCase();
        return this.availableProvinces.filter(province => 
            province.name.toLowerCase().includes(lowerQuery)
        ).slice(0, 10); // Limit to 10 suggestions
    }

    filterMunicipalities(query) {
        if (!this.availableMunicipalities || query.length < 2) {
            return [];
        }
        
        const lowerQuery = query.toLowerCase();
        return this.availableMunicipalities.filter(municipality => 
            municipality.name.toLowerCase().includes(lowerQuery)
        ).slice(0, 10); // Limit to 10 suggestions
    }

    filterStreets(query) {
        if (!this.availableStreets || query.length < 2) {
            return [];
        }
        
        const lowerQuery = query.toLowerCase();
        return this.availableStreets.filter(street => 
            street.name.toLowerCase().includes(lowerQuery) ||
            `${street.type} ${street.name}`.toLowerCase().includes(lowerQuery)
        ).slice(0, 10); // Limit to 10 suggestions
    }

    showProvinceSuggestions(suggestions) {
        const dropdown = document.getElementById('province-dropdown');
        
        if (suggestions.length === 0) {
            dropdown.classList.add('hidden');
            return;
        }
        
        dropdown.innerHTML = suggestions.map(province => 
            `<div class="province-suggestion p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0" 
                  data-code="${province.code}" 
                  data-name="${province.name}">
                <span class="font-medium">${province.name}</span>
            </div>`
        ).join('');
        
        dropdown.classList.remove('hidden');
        
        // Add click listeners to suggestions
        dropdown.querySelectorAll('.province-suggestion').forEach(suggestion => {
            suggestion.addEventListener('click', () => {
                this.selectProvince({
                    code: suggestion.dataset.code,
                    name: suggestion.dataset.name
                });
            });
        });
    }

    showMunicipalitySuggestions(suggestions) {
        const dropdown = document.getElementById('municipality-dropdown');
        
        if (suggestions.length === 0) {
            dropdown.classList.add('hidden');
            return;
        }
        
        dropdown.innerHTML = suggestions.map(municipality => 
            `<div class="municipality-suggestion p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0" 
                  data-code="${municipality.code}" 
                  data-name="${municipality.name}">
                <span class="font-medium">${municipality.name}</span>
            </div>`
        ).join('');
        
        dropdown.classList.remove('hidden');
        
        // Add click listeners to suggestions
        dropdown.querySelectorAll('.municipality-suggestion').forEach(suggestion => {
            suggestion.addEventListener('click', () => {
                this.selectMunicipality({
                    code: suggestion.dataset.code,
                    name: suggestion.dataset.name
                });
            });
        });
    }

    showStreetSuggestions(suggestions) {
        const dropdown = document.getElementById('street-dropdown');
        
        if (suggestions.length === 0) {
            dropdown.classList.add('hidden');
            return;
        }
        
        dropdown.innerHTML = suggestions.map(street => 
            `<div class="street-suggestion p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0" 
                  data-code="${street.code}" 
                  data-name="${street.name}" 
                  data-type="${street.type}">
                <span class="font-medium">${street.type} ${street.name}</span>
            </div>`
        ).join('');
        
        dropdown.classList.remove('hidden');
        
        // Add click listeners to suggestions
        dropdown.querySelectorAll('.street-suggestion').forEach(suggestion => {
            suggestion.addEventListener('click', () => {
                this.selectStreet({
                    code: suggestion.dataset.code,
                    name: suggestion.dataset.name,
                    type: suggestion.dataset.type
                });
            });
        });
    }

    selectProvince(province) {
        const input = document.getElementById('province-input');
        const dropdown = document.getElementById('province-dropdown');
        
        this.selectedProvince = province;
        input.value = province.name;
        dropdown.classList.add('hidden');
        
        // Reset dependent fields
        this.resetMunicipality();
        
        // Load municipalities for selected province
        this.loadMunicipalities(province.code, province.name);
    }

    selectMunicipality(municipality) {
        const input = document.getElementById('municipality-input');
        const dropdown = document.getElementById('municipality-dropdown');
        
        this.selectedMunicipality = municipality;
        input.value = municipality.name;
        dropdown.classList.add('hidden');
        
        // Reset dependent fields
        this.resetStreet();
        
        // Load streets for selected municipality
        if (this.selectedProvince) {
            this.loadStreets(this.selectedProvince.code, municipality.code, municipality.name);
        }
    }

    selectStreet(street) {
        const input = document.getElementById('street-input');
        const dropdown = document.getElementById('street-dropdown');
        
        this.selectedStreet = street;
        input.value = `${street.type} ${street.name}`;
        dropdown.classList.add('hidden');
        
        document.getElementById('search-btn').disabled = true;
    }

    resetMunicipality() {
        const input = document.getElementById('municipality-input');
        const dropdown = document.getElementById('municipality-dropdown');
        const countEl = document.getElementById('municipalities-count');
        
        input.placeholder = 'Selecteer eerst provincie';
        input.disabled = true;
        input.value = '';
        dropdown.classList.add('hidden');
        countEl.textContent = '-';
        
        this.selectedMunicipality = null;
        this.availableMunicipalities = [];
        this.resetStreet();
    }

    resetStreet() {
        const input = document.getElementById('street-input');
        const dropdown = document.getElementById('street-dropdown');
        const countEl = document.getElementById('streets-count');
        const numeroInput = document.getElementById('numero');
        const searchBtn = document.getElementById('search-btn');
        
        input.placeholder = 'Selecteer eerst gemeente';
        input.disabled = true;
        input.value = '';
        dropdown.classList.add('hidden');
        countEl.textContent = '-';
        
        numeroInput.disabled = true;
        numeroInput.value = '';
        searchBtn.disabled = true;
        
        this.selectedStreet = null;
        this.availableStreets = [];
    }

    attachEvents() {
        // Province autocomplete
        const provinceInput = document.getElementById('province-input');
        const provinceDropdown = document.getElementById('province-dropdown');
        
        provinceInput.addEventListener('input', (e) => {
            let query = e.target.value;
            // Limit to 3 characters
            if (query.length > 3) {
                query = query.slice(0, 3);
                provinceInput.value = query;
            }
            if (query.length === 3) {
                const suggestions = this.filterProvinces(query);
                this.showProvinceSuggestions(suggestions);
                // Optionally, force user to select from dropdown
                provinceDropdown.classList.remove('hidden');
            } else {
                provinceDropdown.classList.add('hidden');
                this.selectedProvince = null;
                this.resetMunicipality();
            }
        });

        // Municipality autocomplete
        const municipalityInput = document.getElementById('municipality-input');
        const municipalityDropdown = document.getElementById('municipality-dropdown');
        
        municipalityInput.addEventListener('input', (e) => {
            let query = e.target.value;
            if (query.length > 3) {
                query = query.slice(0, 3);
                municipalityInput.value = query;
            }
            if (query.length === 3) {
                const suggestions = this.filterMunicipalities(query);
                this.showMunicipalitySuggestions(suggestions);
                municipalityDropdown.classList.remove('hidden');
            } else {
                municipalityDropdown.classList.add('hidden');
                this.selectedMunicipality = null;
                this.resetStreet();
            }
        });

        // Street autocomplete
        const streetInput = document.getElementById('street-input');
        const streetDropdown = document.getElementById('street-dropdown');
        
        streetInput.addEventListener('input', (e) => {
            let query = e.target.value;
            if (query.length > 3) {
                query = query.slice(0, 3);
                streetInput.value = query;
            }
            if (query.length === 3) {
                const suggestions = this.filterStreets(query);
                this.showStreetSuggestions(suggestions);
                streetDropdown.classList.remove('hidden');
            } else {
                streetDropdown.classList.add('hidden');
                this.selectedStreet = null;
                document.getElementById('search-btn').disabled = true;
            }
        });

        // Hide dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            if (!provinceInput.contains(e.target) && !provinceDropdown.contains(e.target)) {
                provinceDropdown.classList.add('hidden');
            }
            if (!municipalityInput.contains(e.target) && !municipalityDropdown.contains(e.target)) {
                municipalityDropdown.classList.add('hidden');
            }
            if (!streetInput.contains(e.target) && !streetDropdown.contains(e.target)) {
                streetDropdown.classList.add('hidden');
            }
        });

        // Keyboard navigation for province dropdown
        provinceInput.addEventListener('keydown', (e) => {
            this.handleKeyboardNavigation(e, provinceDropdown, '.province-suggestion');
        });

        // Keyboard navigation for municipality dropdown
        municipalityInput.addEventListener('keydown', (e) => {
            this.handleKeyboardNavigation(e, municipalityDropdown, '.municipality-suggestion');
        });

        // Keyboard navigation for street dropdown
        streetInput.addEventListener('keydown', (e) => {
            this.handleKeyboardNavigation(e, streetDropdown, '.street-suggestion');
        });

        // Search button
        document.getElementById('search-btn').addEventListener('click', () => this.searchProperty());

        // Enter key support
        document.getElementById('numero').addEventListener('keypress', (e) => {
            if (e.key === 'Enter' && !document.getElementById('search-btn').disabled) {
                this.searchProperty();
            }
        });


    }

    handleKeyboardNavigation(e, dropdown, suggestionSelector) {
        const suggestions = dropdown.querySelectorAll(suggestionSelector);
        let currentSelected = dropdown.querySelector(`${suggestionSelector}.bg-blue-100`);
        let currentIndex = Array.from(suggestions).indexOf(currentSelected);

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (currentSelected) currentSelected.classList.remove('bg-blue-100');
                currentIndex = currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0;
                if (suggestions[currentIndex]) {
                    suggestions[currentIndex].classList.add('bg-blue-100');
                    suggestions[currentIndex].scrollIntoView({ block: 'nearest' });
                }
                break;
                
            case 'ArrowUp':
                e.preventDefault();
                if (currentSelected) currentSelected.classList.remove('bg-blue-100');
                currentIndex = currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1;
                if (suggestions[currentIndex]) {
                    suggestions[currentIndex].classList.add('bg-blue-100');
                    suggestions[currentIndex].scrollIntoView({ block: 'nearest' });
                }
                break;
                
            case 'Enter':
                e.preventDefault();
                if (currentSelected) {
                    currentSelected.click();
                }
                break;
                
            case 'Escape':
                dropdown.classList.add('hidden');
                break;
        }
    }

    async searchProperty() {
        if (!this.selectedProvince || !this.selectedMunicipality || !this.selectedStreet) {
            this.showError('Selecteer eerst een provincie, gemeente en straat');
            return;
        }

        const numero = document.getElementById('numero').value.trim();
        if (!numero) {
            this.showError('Voer een huisnummer in');
            return;
        }

        // Get selected service type from the CadastroForm component
        let serviceType = 'energielabel'; // Default
        if (window.getSelectedServiceType) {
            serviceType = window.getSelectedServiceType();
        }
        
        // Convert to display format
        const serviceTypeDisplay = serviceType === 'energielabel' ? 'Certificado' : 'Informe de mejoras';
        
        // Get selected building type
        const buildingTypeSelect = document.getElementById('building-type-card');
        const buildingType = buildingTypeSelect ? buildingTypeSelect.value : 'Appartement';

        const propertyData = {
            // For display purposes
            provincia: this.selectedProvince.name,
            municipio: this.selectedMunicipality.name,
            calle: this.selectedStreet.name,
            numero: numero,
            serviceType: serviceTypeDisplay,
            buildingType: buildingType,
            // For API calls (codes are required)
            codigoProvincia: this.selectedProvince.code,
            codigoMunicipio: this.selectedMunicipality.code,
            codigoVia: this.selectedStreet.code
        };

        // ✅ Bewaar formulierwaarden in sessionStorage voor geval van fouten
        this.saveFormValues();

        // Store property data in sessionStorage voor gebruik in ComparePage
        sessionStorage.setItem('propertySearchData', JSON.stringify(propertyData));
        
        // ✅ Ruim formulierwaarden op na een korte vertraging (zodat eventuele errors ze nog kunnen gebruiken)
        setTimeout(() => {
            // Als we succesvol hebben genavigeerd, ruimen we de form data op
            if (window.location.pathname === '/compare/' || 
                (window.router && window.router.getCurrentPath && window.router.getCurrentPath() === '/compare/')) {
                this.clearSavedFormValues();
            }
        }, 5000); // 5 seconden vertraging om errors de kans te geven de data te gebruiken
        
        // Navigeer naar ComparePage
        if (window.router) {
            window.router.navigate('/compare/');
        } else {
            // Fallback voor directe navigatie
            window.location.href = '/compare/';
        }
    }



    async performSearch(propertyData, searchType) {
        const searchBtn = document.getElementById('search-btn');
        const originalText = searchBtn.textContent;
        
        // Loading state
        searchBtn.disabled = true;
        searchBtn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>Zoeken...';
        
        // Toon laadstatus via PropertyResults component
        this.propertyResults.displayLoading('Eigendomsgegevens ophalen...');

        try {
            console.log(`🔍 Searching property via Vite:`, propertyData);
            
            const result = await this.client.getPropertyData(
                propertyData.codigoProvincia,
                propertyData.codigoMunicipio,
                propertyData.codigoVia,
                propertyData.numero
            );

            // Gebruik PropertyResults component om resultaten te tonen
            this.propertyResults.displayResults(
                result, 
                propertyData, 
                `🎯 Smart Dropdown - ⚡ Vite Direct API`
            );
            
            console.log(`✅ Property data retrieved via ${searchType} search (Vite):`, result);
            
        } catch (error) {
            // Gebruik PropertyResults component om fout te tonen
            this.propertyResults.displayError(
                error.message,
                propertyData,
                `🎯 Smart Dropdown - ⚡ Vite Direct API`
            );
            
            console.error(`❌ Property search failed:`, error);
        } finally {
            searchBtn.disabled = false;
            searchBtn.textContent = originalText;
        }
    }

    createPropertyTable(result, propertyData) {
        try {
            const data = result.consulta_dnplocResult;
            if (!data) return '<p class="text-red-500">Geen data gevonden in API response</p>';

            // Handle different API response structures
            const bi = data.bico?.bi;
            const constructions = data.bico?.lcons || [];
            
            // New structure check
            const hasNewStructure = data.lncdnp !== undefined;
            
            let html = '<div class="space-y-6">';
            
            // Debug info to understand structure
            console.log('API Response structure:', {
                hasBico: !!data.bico,
                hasNewStructure,
                keys: Object.keys(data)
            });

            // Property Information Table
            if (bi) {
                html += `
                <div class="bg-white rounded-lg shadow-sm border">
                    <h5 class="text-lg font-semibold p-4 border-b bg-gray-50 rounded-t-lg">🏠 Eigendomsinformatie</h5>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900 w-1/3">Referentie</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.idbi?.rc?.pc1 || ''} ${bi.idbi?.rc?.pc2 || ''}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Adres</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.ldt || 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Provincie</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.dt?.np || 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Gemeente</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.dt?.nm || 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Postcode</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.dt?.locs?.lous?.lourb?.dp || 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Gebruik</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.debi?.luso || 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Oppervlakte</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.debi?.sfc !== undefined ? bi.debi.sfc + ' m²' : 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Eigendomspercentage</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.debi?.cpt !== undefined ? bi.debi.cpt + '%' : 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">Bouwjaar</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${bi.debi?.ant !== undefined ? bi.debi.ant : 'Niet beschikbaar'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>`;
            }

            // Constructions Table
            if (constructions.length > 0) {
                html += `
                <div class="bg-white rounded-lg shadow-sm border">
                    <h5 class="text-lg font-semibold p-4 border-b bg-gray-50 rounded-t-lg">🏗️ Bouwwerken (${constructions.length})</h5>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-sm">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trap</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verdieping</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deur</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschrijving</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">`;

                constructions.forEach((construction, index) => {
                    html += `
                        <tr class="hover:bg-blue-50">
                            <td class="px-3 py-2">
                                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    construction.lcd === 'VIVIENDA' ? 'bg-green-100 text-green-800' : 
                                    construction.lcd === 'APARCAMIENTO' ? 'bg-orange-100 text-orange-800' :
                                    'bg-blue-100 text-blue-800'
                                }">
                                    ${construction.lcd || 'Onbekend'}
                                </span>
                            </td>
                            <td class="px-3 py-2 text-gray-700 font-mono text-xs">${construction.dt?.lourb?.loint?.es || '-'}</td>
                            <td class="px-3 py-2 text-gray-700 font-mono text-xs">${construction.dt?.lourb?.loint?.pt || '-'}</td>
                            <td class="px-3 py-2 text-gray-700 font-mono text-xs">${construction.dt?.lourb?.loint?.pu || '-'}</td>
                            <td class="px-3 py-2 text-gray-700">
                                <span class="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-xs font-mono">
                                    ${construction.dfcons?.stl || '-'}
                                </span>
                            </td>
                            <td class="px-3 py-2 text-gray-700 text-xs">${construction.dvcons?.dtip || 'Niet beschikbaar'}</td>
                        </tr>`;
                });

                html += `
                            </tbody>
                        </table>
                    </div>
                </div>`;
            }

            // Control Information - handle both old and new structures
            if (data.control || hasNewStructure) {
                const cudnp = data.control?.cudnp;
                const cucons = data.control?.cucons;
                
                html += `
                <div class="bg-white rounded-lg shadow-sm border">
                    <h5 class="text-lg font-semibold p-4 border-b bg-gray-50 rounded-t-lg">ℹ️ Controle Informatie</h5>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900 w-1/3">CUDNP</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${cudnp !== undefined ? cudnp : 'Niet beschikbaar'}</td>
                                </tr>
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">CUCONS</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${cucons !== undefined ? cucons : 'Niet beschikbaar'}</td>
                                </tr>`;
                
                // Add new structure fields if available
                if (hasNewStructure) {
                    html += `
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">LNCDNP</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${data.lncdnp !== undefined ? data.lncdnp : 'Niet beschikbaar'}</td>
                                </tr>`;
                    
                    // Add RC information if available
                    if (data.lncdnp?.rcdnp) {
                        data.lncdnp.rcdnp.forEach((rc, index) => {
                            if (rc.rc) {
                                html += `
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900">RC ${index + 1}</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">
                                        PC1: ${rc.rc.pc1 || ''} | PC2: ${rc.rc.pc2 || ''} | CAR: ${rc.rc.car || ''} | CC1: ${rc.rc.cc1 || ''} | CC2: ${rc.rc.cc2 || ''}
                                    </td>
                                </tr>`;
                            }
                        });
                    }
                }
                
                html += `
                            </tbody>
                        </table>
                    </div>
                </div>`;
            }

            // If new structure and no bico data, show all available data
            if (hasNewStructure && !data.bico) {
                html += `
                <div class="bg-white rounded-lg shadow-sm border">
                    <h5 class="text-lg font-semibold p-4 border-b bg-gray-50 rounded-t-lg">📊 Alle Beschikbare Data</h5>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <tbody class="bg-white divide-y divide-gray-200">`;
                
                // Recursively display all data
                const displayData = (obj, prefix = '') => {
                    let rows = '';
                    for (const [key, value] of Object.entries(obj)) {
                        const fullKey = prefix ? `${prefix}.${key}` : key;
                        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                            rows += displayData(value, fullKey);
                        } else if (Array.isArray(value)) {
                            rows += `
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900 w-1/3">${fullKey} (array)</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${value.length} items</td>
                                </tr>`;
                        } else {
                            rows += `
                                <tr class="hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm font-medium text-gray-900 w-1/3">${fullKey}</td>
                                    <td class="px-4 py-3 text-sm text-gray-700">${value !== undefined ? value : 'Niet beschikbaar'}</td>
                                </tr>`;
                        }
                    }
                    return rows;
                };
                
                html += displayData(data);
                
                html += `
                            </tbody>
                        </table>
                    </div>
                </div>`;
            }

            html += '</div>';
            return html;

        } catch (error) {
            console.error('Error creating property table:', error);
            return `<p class="text-red-500">Fout bij het weergeven van de data: ${error.message}</p>`;
        }
    }

    // ✅ Bewaar alle formulierwaarden in sessionStorage
    saveFormValues() {
        try {
            // Get selected service type
            let serviceType = 'energielabel';
            if (window.getSelectedServiceType) {
                serviceType = window.getSelectedServiceType();
            }

            // Get building type
            const buildingTypeSelect = document.getElementById('building-type-card');
            const buildingType = buildingTypeSelect ? buildingTypeSelect.value : 'Appartement';

            // Get huisnummer
            const numero = document.getElementById('numero').value.trim();

            const formData = {
                // Selected values with full objects
                selectedProvince: this.selectedProvince,
                selectedMunicipality: this.selectedMunicipality,
                selectedStreet: this.selectedStreet,
                
                // Input field values
                provinceInputValue: document.getElementById('province-input')?.value || '',
                municipalityInputValue: document.getElementById('municipality-input')?.value || '',
                streetInputValue: document.getElementById('street-input')?.value || '',
                numero: numero,
                
                // Other form values
                serviceType: serviceType,
                buildingType: buildingType,
                
                // Available options for dropdowns
                availableProvinces: this.availableProvinces,
                availableMunicipalities: this.availableMunicipalities,
                availableStreets: this.availableStreets,
                
                // Timestamp for cleanup
                timestamp: Date.now()
            };

            sessionStorage.setItem('cadastroFormValues', JSON.stringify(formData));
            console.log('✅ Formulierwaarden opgeslagen in sessionStorage');
        } catch (error) {
            console.error('❌ Fout bij opslaan formulierwaarden:', error);
        }
    }

    // ✅ Herstel formulierwaarden uit sessionStorage
    restoreFormValues() {
        try {
            const savedDataString = sessionStorage.getItem('cadastroFormValues');
            if (!savedDataString) return false;

            const savedData = JSON.parse(savedDataString);
            
            // Controleer of de data niet te oud is (max 1 uur)
            const maxAge = 60 * 60 * 1000; // 1 uur
            if (Date.now() - savedData.timestamp > maxAge) {
                sessionStorage.removeItem('cadastroFormValues');
                return false;
            }

            console.log('🔄 Herstellen formulierwaarden...', savedData);

            // Herstel beschikbare opties
            this.availableProvinces = savedData.availableProvinces || [];
            this.availableMunicipalities = savedData.availableMunicipalities || [];
            this.availableStreets = savedData.availableStreets || [];

            // Herstel geselecteerde waarden
            this.selectedProvince = savedData.selectedProvince;
            this.selectedMunicipality = savedData.selectedMunicipality;
            this.selectedStreet = savedData.selectedStreet;

            // Wacht tot DOM elementen geladen zijn
            setTimeout(() => {
                // Herstel provincie
                if (savedData.selectedProvince && savedData.provinceInputValue) {
                    const provinceInput = document.getElementById('province-input');
                    if (provinceInput) {
                        provinceInput.value = savedData.provinceInputValue;
                        provinceInput.disabled = false;
                    }
                }

                // Herstel gemeente
                if (savedData.selectedMunicipality && savedData.municipalityInputValue) {
                    const municipalityInput = document.getElementById('municipality-input');
                    if (municipalityInput) {
                        municipalityInput.value = savedData.municipalityInputValue;
                        municipalityInput.disabled = false;
                        municipalityInput.placeholder = 'Type minimaal 2 karakters...';
                    }
                }

                // Herstel straat
                if (savedData.selectedStreet && savedData.streetInputValue) {
                    const streetInput = document.getElementById('street-input');
                    if (streetInput) {
                        streetInput.value = savedData.streetInputValue;
                        streetInput.disabled = false;
                        streetInput.placeholder = 'Type minimaal 2 karakters...';
                    }
                }

                // Herstel huisnummer
                if (savedData.numero) {
                    const numeroInput = document.getElementById('numero');
                    if (numeroInput) {
                        numeroInput.value = savedData.numero;
                        numeroInput.disabled = false;
                    }
                }

                // Herstel service type
                if (savedData.serviceType && window.setSelectedServiceType) {
                    window.setSelectedServiceType(savedData.serviceType);
                }

                // Herstel building type
                if (savedData.buildingType) {
                    const buildingTypeSelect = document.getElementById('building-type-card');
                    if (buildingTypeSelect) {
                        buildingTypeSelect.value = savedData.buildingType;
                    }
                }

                // Update tellers
                this.updateCounters();

                // Enable zoekknop als alle velden ingevuld zijn
                if (this.selectedProvince && this.selectedMunicipality && this.selectedStreet && savedData.numero) {
                    const searchBtn = document.getElementById('search-btn');
                    if (searchBtn) {
                        searchBtn.disabled = false;
                    }
                }

                console.log('✅ Formulierwaarden hersteld');
            }, 200);

            return true;
        } catch (error) {
            console.error('❌ Fout bij herstellen formulierwaarden:', error);
            sessionStorage.removeItem('cadastroFormValues');
            return false;
        }
    }

    // ✅ Update counters voor dropdowns
    updateCounters() {
        const provinceCount = document.getElementById('provinces-count');
        const municipalityCount = document.getElementById('municipalities-count');
        const streetCount = document.getElementById('streets-count');

        if (provinceCount && this.availableProvinces.length > 0) {
            provinceCount.textContent = `(${this.availableProvinces.length})`;
        }

        if (municipalityCount && this.availableMunicipalities.length > 0) {
            municipalityCount.textContent = `(${this.availableMunicipalities.length})`;
        }

        if (streetCount && this.availableStreets.length > 0) {
            streetCount.textContent = `(${this.availableStreets.length})`;
        }
    }

    // ✅ Ruim opgeslagen formulierwaarden op
    clearSavedFormValues() {
        sessionStorage.removeItem('cadastroFormValues');
        console.log('🧹 Formulierwaarden verwijderd uit sessionStorage');
    }

    showError(message) {
        const resultsDiv = document.getElementById('results');
        if (resultsDiv) {
            resultsDiv.innerHTML = `
                <div class="p-5 rounded-lg mt-5 bg-red-100 border border-red-300 text-red-700">
                    <h4>⚠️ ${message}</h4>
                </div>
            `;
        } else {
            console.error("Element with id 'results' not found.");
        }
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new ViteCatastroApp();
});
}